import { createDecorator, registerSingleton } from "../../instantiation/common/registry.js";
import { SyncDescriptor } from "../../instantiation/common/descriptors.js";
import { Disposable } from "../../../base/common/lifecycle.js";
import { isDevelopmentUrl, isLicenseRequiredHost } from "../../../base/browser/url.js";
import { CommandsRegistry, ICommandService } from "../../commands/common/commands.js";
import { IIdentitiyService } from "../../auth/common/auth.js";
import { ILicenseService } from "../../auth/common/licenses.js";
import { IAnalyticsService } from "../../analytics/common/analytics.js";
import { INotificationService } from "../../notifications/common/notifications.js";
import { ILogService } from "../../log/common/log.js";
import { OpenUpsellDialogCommandId, type UpsellDialogOptions } from "./upsell.js";
import { BUILD_ENVIRONMENT } from "../../../base/common/$__metadata.js";
import { IProductService } from "../../product/common/productService.js";

export const IEditEntitlementService = createDecorator<IEditEntitlementService>("editEntitlementService");

/** One edit attempt asking to pass the gate — the fields feed the upsell dialog
 *  and telemetry. Call sites keep their HISTORICAL analytics event names so
 *  dashboards don't break (property.edit.license_required, nodegraph.…). */
export interface EditGuardRequest {
    /** Document save/load requires Pro even on public hosts and in dev builds. */
    requireLicenseEverywhere?: boolean;
    /** upsell + analytics attribution, e.g. "property_edit" | "nodegraph_edit" | "gizmo_transform" */
    source: string;
    /** the exact trigger, e.g. "edit position on Mesh" — segments the upsell funnel */
    detail?: string;
    /** analytics event fired on a gate hit (default "edit.license_required") */
    analyticsEvent?: string;
    analyticsProps?: Record<string, unknown>;
    /** Command retried by the upsell after access is granted. */
    continueCommand?: { id: string; args?: unknown };
    // dialog copy overrides — defaults pitch the generic editing entitlement
    title?: string;
    subtitle?: string;
    message?: string;
    purchaseLabel?: string;
    /** user-facing fallback when the upsell dialog isn't available (headless) */
    fallbackWarning?: string;
}

export function hasInspectorAccess(license: Pick<ILicenseService, "isInspectorAccessAllowed"> | undefined): boolean {
    return license?.isInspectorAccessAllowed === true;
}

const SCENE_EDIT_SOURCES = new Set([
    "property_edit", "gizmo_transform", "hierarchy_duplicate", "hierarchy_reparent", "render_pass_fly",
]);

/**
 * THE seam for "may the user edit this project right now": editing your own
 * project-under-development is gated behind a Needle Inspector entitlement,
 * public-site inspection stays frictionless. Every edit SURFACE (inspector
 * fields, node graph, transform gizmo, future ones) asks this service instead
 * of re-implementing the identity/license/upsell dance — the gizmo shipped
 * without a gate precisely because the check used to live per-surface.
 */
export interface IEditEntitlementService {
    readonly _serviceBrand: undefined;

    /** Whether this session is a GATED context: localhost/LAN, a cloud
     *  dev/preview URL (the visitor is almost always the author), or a host on
     *  the observed license-required list (a company running its own product on
     *  a public QA/staging host — see isLicenseRequiredHost). Ordinary public
     *  sites stay ungated. Stable for the session. */
    readonly isGatedContext: boolean;

    /** Pure check, no side effects — true when an edit would be allowed now. */
    readonly canEdit: boolean;

    /** Guard an edit attempt: true = proceed. On veto, opens the two-stage
     *  upsell (login → buy) once per burst — a live drag fires the guard many
     *  times — and returns false. While the license snapshot is still loading
     *  we veto (the dialog shows a brief "checking…" that auto-resolves), so
     *  the paywall never leaks free edits. */
    guardEdit(request: EditGuardRequest): boolean;
}

/** ?edit-gate on the page URL: re-arm the paywall in a dev build. */
function isEditGateForced(): boolean {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).has("edit-gate");
}

export class EditEntitlementService extends Disposable implements IEditEntitlementService {
    declare readonly _serviceBrand: undefined;

    readonly isGatedContext = typeof window !== "undefined"
        && (isDevelopmentUrl(window.location.href) || isLicenseRequiredHost(window.location.href));

    /** Set while an upsell dialog is pending — shared across ALL edit surfaces,
     *  so a drag that trips several guards opens one dialog, not a stack. */
    private _upsellPending = false;

    constructor(
        @IIdentitiyService private readonly identity: IIdentitiyService,
        @ILicenseService private readonly license: ILicenseService,
        @ICommandService private readonly commands: ICommandService,
        @IAnalyticsService private readonly analytics: IAnalyticsService,
        @INotificationService private readonly notifications: INotificationService,
        @ILogService private readonly logger: ILogService,
        @IProductService private readonly product: IProductService,
    ) {
        super();
    }

    get canEdit(): boolean {
        if (!this.isGatedContext) return true;
        // dev builds are not a sales context — the gate would otherwise block
        // every logged-out edit in the dev loop and the browser test suites.
        // ?edit-gate re-arms the paywall for testing the upsell funnel itself.
        // Read live (not cached) so the flag toggles without a reload.
        if (BUILD_ENVIRONMENT === "dev" && !isEditGateForced()) return true;
        return hasInspectorAccess(this.license);
    }

    guardEdit(request: EditGuardRequest): boolean {
        if (this.product.unlicensedPermissions?.["scene.edit"] === true && !request.requireLicenseEverywhere
            && SCENE_EDIT_SOURCES.has(request.source)) return true;
        if (request.requireLicenseEverywhere ? hasInspectorAccess(this.license) : this.canEdit) return true;
        this.logger.info("EditEntitlement: edit gated", {
            source: request.source,
            detail: request.detail,
            loggedIn: this.identity.isLoggedIn,
            licensed: this.license.isInspectorLicensed,
            accessReason: this.license.inspectorAccessReason,
            cached: this.license.licenses?.length ?? null,
        });
        if (!this._upsellPending) {
            this._upsellPending = true;
            this.analytics?.logEvent(request.analyticsEvent ?? "edit.license_required", {
                detail: request.detail,
                loggedIn: this.identity.isLoggedIn,
                ...request.analyticsProps,
            });
            if (CommandsRegistry.getCommand(OpenUpsellDialogCommandId)) {
                const options: UpsellDialogOptions = {
                    source: request.source,
                    detail: request.detail,
                    requiresLicense: true,
                    title: request.title ?? "Editing is a Pro feature",
                    subtitle: request.subtitle,
                    // not "on local projects" — the gate also covers listed
                    // hosts, where that wording would just read as wrong
                    message: request.message
                        ?? "Editing this project requires Needle Inspector Pro (included with Needle Engine Pro). Sign in with a free account to continue.",
                    purchaseLabel: request.purchaseLabel ?? "Get Needle Inspector Pro",
                    continueCommand: request.continueCommand,
                };
                void this.commands.executeCommand(OpenUpsellDialogCommandId, options);
            }
            else if (request.fallbackWarning) {
                this.notifications.warn(request.fallbackWarning);
            }
            else {
                this.logger.warn(`EditEntitlement: license required to edit (${request.source}); upsell command not registered.`);
            }
            // Allow re-opening after the burst of live edits settles / dialog closes.
            setTimeout(() => { this._upsellPending = false; }, 1500);
        }
        return false;
    }
}

registerSingleton(IEditEntitlementService, new SyncDescriptor(EditEntitlementService));
